import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { runInAction } from 'mobx';
import { useRouter } from 'next/router';
import { Grid } from 'theme-ui';
import SectionTitle from '@features/layout/components/SectionTitle/SectionTitle';
import styles from '@features/productPreview/sections/RelatedProducts/RelatedProducts.styles';
import { IRecommendedProductResponse, IRelatedProductResponse } from '@features/productPreview/typescript';
import { WindowType } from '@typescript/index';
import CategoriesProgressLine from '@features/categories/components/CategoriesProgressLine/CategoriesProgressLine';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import ProductState from '@features/categories/mobx/state/ProductState';
import NextProductApi from '@features/api/nextApiService/nextProductService';
import { useOffer } from '@hooks/useOffer';
import { PIMCORE_LOCALE_FALLBACK } from '@config/index';
import Loading from '@components/Loading/Loading';
import Button from '@components/Button/Button';
import getPublicEnv from '@utils/getPublicEnv';
import ProductList from '@components/ProductList/ProductList';
const PageSizeOnMobile = 4;
const PageSizeOnDesktop = 8;
type PropsType = {
  mainProductID: number;
  windowType: WindowType;
  initialResponse: IRecommendedProductResponse | null;
};
const RelatedProducts = ({
  mainProductID,
  windowType,
  initialResponse
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const [offerName, priceListName, storageName] = useOffer();
  const {
    locale
  } = useRouter();
  const [products, setProducts] = useState<ProductState[]>([]);
  const langLocale = locale ? PIMCORE_LOCALE_FALLBACK[locale] : PIMCORE_LOCALE_FALLBACK[getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE')];
  const [isComponentLoading, setIsComponentLoading] = useState(false);
  const [isMoreProductsLoading, setIsMoreProductsLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [maxProducts, setMaxProducts] = useState(0);
  const showMoreItems: boolean = products.length !== maxProducts;
  const isMobile = windowType === WindowType.mobile;
  const isTablet = windowType === WindowType.tablet;
  const isSmallerThanDesktop = isTablet || isMobile;
  const pageSize = isSmallerThanDesktop ? PageSizeOnMobile : PageSizeOnDesktop;
  const createProducts = (response: IRelatedProductResponse | null): ProductState[] => {
    const fetchProducts: ProductState[] = [];
    if (!response) return fetchProducts;
    const {
      products: newProducts
    } = response;
    if (!newProducts || newProducts.length === 0) return fetchProducts;
    runInAction(() => {
      newProducts.forEach(singleProduct => {
        fetchProducts.push(new ProductState(singleProduct));
      });
    });
    return fetchProducts;
  };
  const fetchRelatedProducts = async (currentOffset: number) => {
    const relatedProductsResponse = await NextProductApi.getRelatedProducts(mainProductID.toString(), offerName, priceListName, storageName, langLocale, currentOffset, pageSize);
    const preparedFetchProducts = createProducts(relatedProductsResponse);
    const newProducts = currentOffset === 0 ? preparedFetchProducts : [...products, ...preparedFetchProducts];
    setProducts(newProducts);
    if (relatedProductsResponse && relatedProductsResponse.pagination) setMaxProducts(relatedProductsResponse.pagination.count);
  };
  const fetchAndSetRelatedProducts = async (currentOffset = 0) => {
    if (currentOffset !== 0 && products.length !== 0 && maxProducts === products.length) return;
    if (currentOffset === 0) {
      setIsComponentLoading(true);
    } else {
      setIsMoreProductsLoading(true);
    }
    await fetchRelatedProducts(currentOffset);
    setOffset(currentOffset);
    setIsComponentLoading(false);
    setIsMoreProductsLoading(false);
  };
  useEffect(() => {
    if (initialResponse) {
      const preparedFetchProducts = createProducts(initialResponse as IRelatedProductResponse);
      setProducts(preparedFetchProducts);
      setMaxProducts(initialResponse.products.length);
    } else {
      fetchAndSetRelatedProducts();
    }
  }, [mainProductID]);
  const handleShowMoreProducts = () => {
    fetchAndSetRelatedProducts(offset + pageSize);
  };
  const renderBottomPanel = () => <>
      {showMoreItems && <>
          <div sx={styles.progressLineSize}>
            <CategoriesProgressLine actualCount={products.length} maxCount={maxProducts} />
          </div>
          <Grid columns={1} sx={styles.buttonCenter}>
            <span data-cy="VoxButtonPagination">
              <Button variant="outlined" variantColor="dark" size="small" letterCase="uppercase" onClick={handleShowMoreProducts}>
                {t('seeMore')}
              </Button>
            </span>
          </Grid>
        </>}
    </>;
  const renderContent = () => <>
      <div sx={styles.titleContainer}>
        <SectionTitle title={t('relatedProductsTitle')} />
      </div>
      <Grid columns={24} sx={styles.productsRow}>
        <ProductList products={products} productsCount={products.length} listingType="related_products" listingId={mainProductID} />
      </Grid>
      <div sx={styles.container}>
        {isMoreProductsLoading ? <div sx={styles.loader}>
            <Loading />
          </div> : renderBottomPanel()}
      </div>
    </>;
  return products.length > 0 ? <ErrorBoundary>
      <section id="collection" sx={styles.section}>
        {isComponentLoading ? <Loading /> : renderContent()}
      </section>
    </ErrorBoundary> : null;
};
export default RelatedProducts;